import { BRANDS, BRAND_DOMAIN, DEFAULT_LOCAL_ENV, API_ACCESS_TOKEN } from "../constants";
export const noop = () => {};
export const defer = (func, timeout = 0) => setTimeout(func, timeout);
export const getQueryString = name => {
  var result = window.location.href.match(new RegExp("[?&]" + name + "=([^&]+)", "i"));

  if (result == null || result.length < 1) {
    return undefined;
  }

  return decodeURIComponent(result[1]);
};
/**
 * Is the brand valid?
 * @param {string} brandCode
 * @returns {boolean}
 */

export function isBrand(brand) {
  return brand in BRANDS;
}
/**
 * Identify the current environment based on the window.location
 * @param {string} brandCode
 * @returns {string} Environment identifier
 */

export const getEnvironment = brandCode => {
  const hostname = window.location.hostname;
  const brandDomain = BRAND_DOMAIN[brandCode];
  const isVirtualBoxHost = hostname === "10.0.2.2";

  if (hostname === "localhost" || isVirtualBoxHost || hostname.includes('ngrok.io')) {
    return "local";
  } else if (hostname.includes("otrldev.uk")) {
    return "test";
  } else if (hostname.includes("stage.otrl.io") || hostname.includes("widgets-staging." + brandDomain)) {
    return "otrl-stage";
  } else if (hostname.includes("staging." + brandDomain)) {
    return "brochure-stage";
  }

  return "prod";
};
/**
 * Get the API access token for the current environment
 * @param {string} env - see `getEnvironment(...)`
 * @param {string=} [localEnv] - Override the environment for local dev ("stage"|"prod"|sidBoxName)
 * @returns {string} An access token
 */

export const getApiAccessToken = (env, localEnv = DEFAULT_LOCAL_ENV) => {
  switch (env) {
    case "brochure-stage":
    case "prod":
      return API_ACCESS_TOKEN.prod;

    case "otrl-stage":
      return API_ACCESS_TOKEN.stage;

    case "local":
      return localEnv === "stage" ? API_ACCESS_TOKEN.stage : localEnv === "prod" ? API_ACCESS_TOKEN.prod : API_ACCESS_TOKEN.default;

    default:
      return API_ACCESS_TOKEN.default;
  }
};
/**
 * Get the available host URLs for the given brand
 * @param {string} brandCode
 * @param {string} env - see `getEnvironment(...)`
 * @param {string=} [localEnv] - Override the environment for local dev ("stage"|"prod"|sidBoxName)
 * @returns {{bookingEngine: string, delayRepay: string, autoDelayRepay: string, api: string}} URLs
 */

export const getHostUrls = (brandCode, env, localEnv = DEFAULT_LOCAL_ENV) => {
  const brandName = BRANDS[brandCode];
  const brandDomain = BRAND_DOMAIN[brandCode];
  const bookingEngineHost = getBookingEngineHost(brandCode, env, localEnv);
  const DR = "delayrepay";
  const legacyDR = "delayrepay-ie";

  switch (env) {
    case "local":
      const remoteUrl = localEnv === "stage" ? "stage.otrl.io" : localEnv === "prod" ? BRAND_DOMAIN[brandCode] : `${localEnv}.otrldev.uk`;
      return {
        bookingEngine: bookingEngineHost,
        delayRepay: `https://${DR}-${brandName}.${remoteUrl}`,
        autoDelayRepay: `https://${legacyDR}-${brandName}.${remoteUrl}`,
        api: `https://api-${brandName}.${remoteUrl}`
      };

    case "test":
    case "otrl-stage":
      return {
        bookingEngine: bookingEngineHost,
        delayRepay: bookingEngineHost.replace(`${brandName}.`, `${DR}-${brandName}.`),
        autoDelayRepay: bookingEngineHost.replace(`${brandName}.`, `${legacyDR}-${brandName}.`),
        api: bookingEngineHost.replace(`${brandName}.`, `api-${brandName}.`)
      };

    case "brochure-stage":
    case "prod":
    default:
      return {
        bookingEngine: `https://ticket.${brandDomain}`,
        delayRepay: `https://${DR}.${brandDomain}`,
        autoDelayRepay: `https://${legacyDR}.${brandDomain}`,
        api: brandCode === 'SR' ? `https://api-${brandName}.otrl.io` : `https://api.${brandDomain}`
      };
  }
};
/**
 * Get the booking-engine URL for the current brand & env based on the current window.location
 *
 * @param {string} brandCode
 * @param {string} env - see `getEnvironment(...)`
 * @param {string} localEnv - Override the environment for local dev ("stage"|"prod"|sidBoxName)
 * @returns {string} booking engine host
 */

export const getBookingEngineHost = (brandCode, env, localEnv = "stage") => {
  const brandName = BRANDS[brandCode];
  const brandDomain = BRAND_DOMAIN[brandCode];
  const _window$location = window.location,
        origin = _window$location.origin,
        port = _window$location.port;

  switch (env) {
    case "local":
      return origin.replace(`:${port}`, `:8080/${brandName}.${localEnv}`);

    case "test":
      return origin.replace(/widgets\./, `${brandName}.`);

    case "otrl-stage":
      return `https://${brandName}.stage.otrl.io`;

    case "brochure-stage":
      return origin.replace(/www\.staging\./, "ticket.");

    case "prod":
    default:
      return `https://ticket.${brandDomain}`;
  }
};